/**
 * The Platform seam: everything the shared client core needs from its host that is not plain ECMAScript. The web
 * shell installs `browserPlatform()` (apps/client/src/browser-platform.ts) at boot; a native shell installs its own
 * (MMKV storage, AppState visibility, expo-haptics…). Until a shell installs one, `getPlatform()` is a headless
 * platform: no storage, no haptics, setInterval timers, a 1280×720 viewport — what Node tests and tools get.
 *
 * Core code never touches `window`, `document`, `localStorage`, `navigator` or `import.meta` directly: it asks
 * `getPlatform()` (or takes the seam as an injected dependency, like `NetEnv` or `MatchHostEnv`).
 */
import { createIntervalScheduler, type IntervalScheduler, type VisibilitySource } from './loop/scheduler';

/** Synchronous string storage (web: localStorage; native: MMKV). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Build-time configuration (web: Vite's `import.meta.env`; native: the app config / OTA manifest). */
export interface PlatformConfig {
  /** A development build (web: `import.meta.env.DEV`). */
  dev: boolean;
  /** Build mode: 'production' | 'development' | 'test' | … (web: `import.meta.env.MODE`). */
  mode: string;
  /** CI build id (web: `VITE_BUILD_ID`; native: the OTA update id), null when unknown. */
  buildId: string | null;
  /** Prefix of root-relative asset URLs (web: `import.meta.env.BASE_URL`). */
  baseUrl: string;
}

/** Short haptic pulses (touch-button presses, big hits). Strength 0..1. */
export interface Haptics {
  pulse(strength: number, durationMs: number): void;
}

export interface PlatformLog {
  info(...a: unknown[]): void;
  warn(...a: unknown[]): void;
  error(...a: unknown[]): void;
}

export interface Platform {
  /** Persistent key/value storage (settings, tutorial progress), or null when unavailable / blocked. */
  storage(): KeyValueStorage | null;
  /** Monotonic ms. */
  now(): number;
  /** App / page visibility (hidden tab, backgrounded app), or null when the host has no such notion. */
  visibility(): VisibilitySource | null;
  /** Timers for the online match's background pump (web: worker-backed so hidden tabs are not throttled). */
  createScheduler(): IntervalScheduler;
  /** Haptics, or null. */
  readonly haptics: Haptics | null;
  readonly log: PlatformLog;
  /** Resolves when the UI fonts are ready to draw with (null = nothing to wait for). */
  fontsReady(): Promise<void> | null;
  /** URL / URI of a root-relative static asset (e.g. 'assets/manifest.json'). */
  assetUrl(path: string): string;
  /** Logical size of the game surface in CSS px / points. */
  viewport(): { w: number; h: number };
  readonly config: PlatformConfig;
}

const joinUrl = (base: string, path: string): string =>
  base.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');

/** The platform Node tests and tools get: nothing persists, nothing vibrates. */
export const headlessPlatform = (overrides: Partial<Platform> = {}): Platform => {
  const config: PlatformConfig = { dev: false, mode: 'test', buildId: null, baseUrl: '/' };
  return {
    storage: () => null,
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    visibility: () => null,
    createScheduler: () => createIntervalScheduler(),
    haptics: null,
    log: console,
    fontsReady: () => null,
    assetUrl: (path) => joinUrl(overrides.config?.baseUrl ?? config.baseUrl, path),
    viewport: () => ({ w: 1280, h: 720 }),
    config,
    ...overrides,
  };
};

/**
 * What read storage from the BUILT-IN headless default (i.e. before any shell installed a platform): module-level
 * singletons (the UI store's settings, Football 101 progress) bind their storage when they are created, so a shell
 * that installs its platform after they were imported runs on no storage at all — saved settings, progress and the
 * session token silently ignored. `setPlatform` reports it (dev builds) so a wrong import order fails loudly.
 */
const earlyReads = new Set<string>();

const fallbackPlatform = (): Platform => {
  const p = headlessPlatform();
  return {
    ...p,
    storage: () => {
      const frames = (new Error().stack ?? '').split('\n').map((l) => l.trim());
      earlyReads.add(frames.find((l) => l.startsWith('at ') && !/platform/i.test(l)) ?? 'storage()');
      return p.storage();
    },
  };
};

const fallback = fallbackPlatform();
let current: Platform = fallback;

/** The installed platform (headless until a shell calls `setPlatform`). */
export const getPlatform = (): Platform => current;

/** Storage was read from the built-in headless default before a shell installed its platform. */
export const storageReadBeforeInstall = (): boolean => earlyReads.size > 0;

/**
 * Install the host platform. Call once at boot, before anything reads settings (web: `install-platform.ts` is
 * main.tsx's first import; native: the app entry, before importing any `@hf/client-core/ui-model` module). Returns
 * the previous one. In a dev build it warns when storage was already read from the headless default: whatever read
 * it (the `ui` store, tutorial progress) is bound to no storage and will neither load nor save.
 */
export const setPlatform = (p: Platform): Platform => {
  const prev = current;
  current = p;
  if (prev === fallback && earlyReads.size > 0 && p.config.dev && p.storage() !== null) {
    p.log.warn(
      `[platform] storage was read before setPlatform() (settings / progress modules imported first): they run ` +
        `without persistence. Install the platform before importing @hf/client-core/ui-model. First read at: ` +
        [...earlyReads][0],
    );
  }
  return prev;
};
